import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { FULL_MONTHS, qaStatusColor, qaStatus, classNames, thisMonth, thisYear } from "../lib/utils";
import { Printer, ArrowLeft, FileDown } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function QAReportPage() {
  const [params] = useSearchParams();
  const home = params.get("home") || "";
  const month = +(params.get("month") || thisMonth());
  const year = +(params.get("year") || thisYear());
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!home) return;
    api.get("/qa/audit", { params: { home, month, year } }).then(({ data }) => setData(data));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [home, month, year]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/reports/qa-audit.pdf", {
        params: { home, month, year }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `NestVillage-QA-${home}-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF generation failed");
    } finally { setDownloading(false); }
  };

  if (!home) return <div className="p-8 text-slate-500">Missing home parameter.</div>;
  if (!data?.audit) return <div className="p-8 text-slate-500">No audit found for {home} · {FULL_MONTHS[month - 1]} {year}.</div>;

  const { audit } = data;
  const ms = data.metric_scores || [];
  const byCat = {};
  ms.forEach((s) => {
    byCat[s.category_name] = byCat[s.category_name] || [];
    byCat[s.category_name].push(s);
  });

  return (
    <div className="bg-slate-100 min-h-screen">
      <div className="no-print bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <Link to="/qa/dashboard" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"><ArrowLeft size={14} /> Back</Link>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadPdf} disabled={downloading || !data?.audit}
            className="inline-flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50"
            data-testid="qa-download-pdf-btn"
            title="Download a branded server-rendered PDF (board-ready)"
          >
            <FileDown size={15} /> {downloading ? "Generating…" : "Download Branded PDF"}
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-sky-800 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-sky-900" data-testid="qa-print-btn">
            <Printer size={15} /> Print / Save PDF
          </button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-8 print-area">
        <div className="bg-white shadow-sm rounded-lg p-10 print:shadow-none print:rounded-none">
          <header className="flex items-center justify-between border-b border-slate-200 pb-5">
            <div className="flex items-center gap-3">
              <img
                src="/nestvillage-logo-transparent.png"
                alt="NestVillage LLC"
                className="h-14 w-14 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <p className="font-heading font-bold text-lg text-slate-900">NestVillage LLC</p>
                <p className="text-[11.5px] uppercase tracking-wider font-semibold text-fuchsia-600">Respect · Dignity · Empathy</p>
                <p className="text-xs text-slate-500">QA Audit Report</p>
              </div>
            </div>
            <p className="text-xs text-slate-400">Generated {new Date(audit.generated_date || Date.now()).toLocaleString()}</p>
          </header>

          <section className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div><span className="text-small-caps">Home</span><p className="font-semibold text-slate-800 mt-0.5">{audit.home}</p></div>
            <div><span className="text-small-caps">Period</span><p className="font-semibold text-slate-800 mt-0.5">{FULL_MONTHS[audit.audit_month - 1]} {audit.audit_year}</p></div>
            <div><span className="text-small-caps">Completed by</span><p className="font-semibold text-slate-800 mt-0.5">{audit.completed_by}</p></div>
            <div><span className="text-small-caps">Overall</span>
              <div className="mt-0.5 flex items-baseline gap-2">
                <span className="font-heading font-bold text-2xl text-slate-900 tabular-nums">{audit.overall_score_percent}%</span>
                <span className={classNames("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border", qaStatusColor(audit.overall_status))}>{audit.overall_status}</span>
              </div>
            </div>
          </section>

          <section className="mt-8">
            <h2 className="text-h2">Category Overview</h2>
            <table className="w-full text-sm mt-3 border border-slate-200">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr><th className="px-4 py-2 text-left">Category</th><th className="px-3 py-2 text-right">Score</th><th className="px-3 py-2 text-left">Status</th></tr>
              </thead>
              <tbody>
                {audit.category_scores.map((c) => {
                  const status = qaStatus(c.score);
                  return (
                    <tr key={c.category_id} className="border-t border-slate-100">
                      <td className="px-4 py-2 font-medium text-slate-800">{c.category_name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.score?.toFixed(1)}%</td>
                      <td className="px-3 py-2"><span className={classNames("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border", qaStatusColor(status))}>{status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          <section className="mt-8">
            <h2 className="text-h2">KPI Details by Category</h2>
            {Object.entries(byCat).map(([name, items]) => (
              <div key={name} className="mt-4">
                <h3 className="text-h3">{name}</h3>
                <table className="w-full text-sm mt-2 border border-slate-200">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                    <tr><th className="px-4 py-2 text-left">Metric</th><th className="px-3 py-2 text-left">Target</th><th className="px-3 py-2 text-right">Score</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Notes</th></tr>
                  </thead>
                  <tbody>
                    {items.map((s) => (
                      <tr key={s.metric_id} className="border-t border-slate-100">
                        <td className="px-4 py-2 font-medium text-slate-800">{s.metric_name}</td>
                        <td className="px-3 py-2 text-slate-500">{s.target}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{s.score_percent}%</td>
                        <td className="px-3 py-2"><span className={classNames("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border", qaStatusColor(s.status))}>{s.status}</span></td>
                        <td className="px-3 py-2 text-slate-600">{s.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </section>

          {audit.manager_notes && (
            <section className="mt-8">
              <h2 className="text-h2">Manager Notes</h2>
              <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{audit.manager_notes}</p>
            </section>
          )}

          <footer className="mt-12 pt-5 border-t border-slate-200 text-center">
            <p className="text-xs text-slate-400 italic">Confidential — for internal NestVillage LLC use only.</p>
          </footer>
        </div>
      </div>
    </div>
  );
}
